import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Clock,
  AlertTriangle,
  Package,
  Wrench,
  Truck,
  Download,
  Ticket,
  Users,
  Activity,
  RefreshCw,
  Filter,
  Calendar
} from 'lucide-react';

const ReportsPage = () => {
  const { plants, selectedPlant } = useData();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tickets');
  const [activeSubTab, setActiveSubTab] = useState('priority');
  
  // Filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Report data states
  const [reportData, setReportData] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState('');

  const fetchEquipment = useCallback(async () => {
    try {
      const params = selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {};
      const response = await api.get('/equipment', { params });
      setEquipment(response.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  }, [selectedPlant]);

  useEffect(() => {
    fetchEquipment();
  }, [selectedPlant, fetchEquipment]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      let endpoint = '';
      
      // Determine endpoint based on active tabs
      if (activeTab === 'tickets') {
        switch (activeSubTab) {
          case 'priority': endpoint = '/reports/tickets/by-priority'; break;
          case 'requester': endpoint = '/reports/tickets/by-requester'; break;
          case 'assignee': endpoint = '/reports/tickets/by-assignee'; break;
          case 'evolution': endpoint = '/reports/tickets/evolution'; break;
          case 'status': endpoint = '/reports/tickets/by-status'; break;
          default: endpoint = '/reports/tickets/by-priority';
        }
      } else if (activeTab === 'equipment') {
        switch (activeSubTab) {
          case 'interventions': endpoint = '/reports/equipment/top-interventions'; break;
          case 'costs': endpoint = '/reports/equipment/top-costs'; break;
          case 'failures': endpoint = '/reports/equipment/failure-rate'; break;
          default: endpoint = '/reports/equipment/top-interventions';
        }
      } else if (activeTab === 'interventions') {
        switch (activeSubTab) {
          case 'type': endpoint = '/reports/interventions/by-type'; break;
          case 'delayed': endpoint = '/reports/interventions/delayed'; break;
          case 'user': endpoint = '/reports/interventions/by-user'; break;
          case 'mttr': endpoint = '/reports/interventions/mttr'; break;
          case 'evolution': endpoint = '/reports/interventions/evolution'; break;
          default: endpoint = '/reports/interventions/by-type';
        }
      } else if (activeTab === 'consumables') {
        switch (activeSubTab) {
          case 'importance': endpoint = '/reports/consumables/by-importance'; break;
          case 'status': endpoint = '/reports/consumables/by-status'; break;
          case 'lowstock': endpoint = '/reports/consumables/low-stock'; break;
          default: endpoint = '/reports/consumables/by-importance';
        }
      } else if (activeTab === 'sla') {
        endpoint = '/reports/sla';
      }
      
      if (endpoint) {
        const response = await api.get(endpoint, { params });
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Eroare la incarcarea raportului');
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeSubTab, selectedPlant, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [activeTab, activeSubTab, selectedPlant, dateFrom, dateTo, fetchReport]);

  const renderBarChart = (data, valueKey = 'value', labelKey = 'name') => {
    if (!data || data.length === 0) {
      return <p className="text-slate-500 text-center py-8">Nu exista date pentru acest raport</p>;
    }
    
    const maxValue = Math.max(...data.map(d => d[valueKey] || 0));
    
    return (
      <div className="space-y-3">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-4">
            <div className="w-32 text-sm text-slate-600 truncate" title={item[labelKey]}>
              {item[labelKey]}
            </div>
            <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden">
              <div 
                className="h-full rounded-full flex items-center justify-end pr-2 text-white text-sm font-medium transition-all"
                style={{ 
                  width: `${maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0}%`,
                  backgroundColor: item.color || '#0052CC',
                  minWidth: item[valueKey] > 0 ? '40px' : '0'
                }}
              >
                {item[valueKey]}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPieChart = (data) => {
    if (!data || data.length === 0) return null;
    
    const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
    
    return (
      <div className="flex flex-wrap gap-4 justify-center">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: item.color || '#0052CC' }}
            />
            <span className="text-sm font-medium">{item.name}</span>
            <Badge variant="outline">{item.value} ({total > 0 ? Math.round(item.value / total * 100) : 0}%)</Badge>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Rapoarte</h1>
          <p className="text-slate-600 mt-1">Analizeaza performanta si datele operationale</p>
        </div>
        <Button onClick={fetchReport} variant="outline" className="h-11" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizeaza
        </Button>
      </div>

      {/* Filters */}
      <Card className="card-industrial">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtre:</span>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">De la:</Label>
              <Input 
                type="date" 
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Pana la:</Label>
              <Input 
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-40"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
              >
                Reseteaza
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setActiveSubTab(''); }}>
        <TabsList className="grid w-full grid-cols-5 h-12">
          <TabsTrigger value="tickets" className="h-10 text-xs md:text-sm" data-testid="tab-tickets">
            <Ticket className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Tichete</span>
          </TabsTrigger>
          <TabsTrigger value="equipment" className="h-10 text-xs md:text-sm" data-testid="tab-equipment">
            <Package className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Echipamente</span>
          </TabsTrigger>
          <TabsTrigger value="interventions" className="h-10 text-xs md:text-sm" data-testid="tab-interventions">
            <Wrench className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Interventii</span>
          </TabsTrigger>
          <TabsTrigger value="consumables" className="h-10 text-xs md:text-sm" data-testid="tab-consumables">
            <TrendingUp className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Consumabile</span>
          </TabsTrigger>
          <TabsTrigger value="sla" className="h-10 text-xs md:text-sm" data-testid="tab-sla">
            <Clock className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">SLA</span>
          </TabsTrigger>
        </TabsList>

        {/* Tickets Reports */}
        <TabsContent value="tickets" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: 'priority', label: 'Dupa Prioritate' },
              { key: 'status', label: 'Dupa Status' },
              { key: 'requester', label: 'Dupa Solicitant' },
              { key: 'assignee', label: 'Dupa Persoana Asignata' },
              { key: 'evolution', label: 'Evolutie in Timp' }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activeSubTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveSubTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Raport Tichete</CardTitle>
              <CardDescription>
                {activeSubTab === 'priority' && 'Distributia tichetelor pe nivele de prioritate'}
                {activeSubTab === 'status' && 'Distributia tichetelor pe status'}
                {activeSubTab === 'requester' && 'Top solicitanti dupa numarul de tichete'}
                {activeSubTab === 'assignee' && 'Distributia tichetelor pe persoana asignata'}
                {activeSubTab === 'evolution' && 'Evolutia tichetelor in timp'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
                </div>
              ) : (
                <>
                  {reportData?.chart_data && renderBarChart(reportData.chart_data)}
                  {reportData?.total !== undefined && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-slate-500">Total tichete: <strong>{reportData.total}</strong></p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Equipment Reports */}
        <TabsContent value="equipment" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: 'interventions', label: 'Top Numar Interventii' },
              { key: 'costs', label: 'Top Costuri' },
              { key: 'failures', label: 'Rata Defectiunilor' }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activeSubTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveSubTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Raport Echipamente</CardTitle>
              <CardDescription>
                {activeSubTab === 'interventions' && 'Echipamentele cu cele mai multe interventii'}
                {activeSubTab === 'costs' && 'Echipamentele cu cele mai mari costuri de mentenanta'}
                {activeSubTab === 'failures' && 'Rata defectiunilor si MTBF per echipament'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
                </div>
              ) : (
                <>
                  {activeSubTab === 'failures' && reportData?.data ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Echipament</th>
                            <th className="text-right p-2">Defectiuni</th>
                            <th className="text-right p-2">MTBF (zile)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.data.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              <td className="p-2">{item.name}</td>
                              <td className="text-right p-2 font-medium text-red-600">{item.failures}</td>
                              <td className="text-right p-2">{item.mtbf_days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    reportData?.chart_data && renderBarChart(reportData.chart_data)
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interventions Reports */}
        <TabsContent value="interventions" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: 'type', label: 'Dupa Tip' },
              { key: 'delayed', label: 'Intarziate' },
              { key: 'user', label: 'Per Utilizator' },
              { key: 'mttr', label: 'MTTR' },
              { key: 'evolution', label: 'Evolutie' }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activeSubTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveSubTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Raport Interventii</CardTitle>
              <CardDescription>
                {activeSubTab === 'type' && 'Distributia interventiilor pe tip (preventiva/corectiva/predictiva)'}
                {activeSubTab === 'delayed' && 'Interventii intarziate fata de planificare'}
                {activeSubTab === 'user' && 'Interventii pe utilizator/tehnician'}
                {activeSubTab === 'mttr' && 'Media timpilor de reparatie (Mean Time To Repair)'}
                {activeSubTab === 'evolution' && 'Evolutia interventiilor in timp'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
                </div>
              ) : (
                <>
                  {activeSubTab === 'mttr' && reportData ? (
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-6 text-center">
                          <p className="text-4xl font-bold text-blue-700">{reportData.overall_mttr_hours}h</p>
                          <p className="text-sm text-blue-600 mt-1">MTTR Mediu Global</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-50">
                        <CardContent className="p-6">
                          <h4 className="font-medium mb-3">MTTR pe Tip Interventie:</h4>
                          <div className="space-y-2">
                            {Object.entries(reportData.by_type || {}).map(([type, hours]) => (
                              <div key={type} className="flex justify-between">
                                <span className="capitalize">{type}</span>
                                <strong>{hours}h</strong>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : activeSubTab === 'delayed' && reportData?.data ? (
                    <div className="space-y-3">
                      {reportData.data.length === 0 ? (
                        <p className="text-center py-8 text-green-600">Nu exista interventii intarziate!</p>
                      ) : (
                        reportData.data.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                            <div>
                              <p className="font-medium">{item.title}</p>
                              <p className="text-sm text-slate-500">{item.equipment_name}</p>
                            </div>
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                              {item.days_late} zile intarziere
                            </Badge>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    reportData?.chart_data && renderBarChart(reportData.chart_data, 'total', 'name')
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consumables Reports */}
        <TabsContent value="consumables" className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: 'importance', label: 'Dupa Importanta' },
              { key: 'status', label: 'Dupa Status' },
              { key: 'lowstock', label: 'Stoc Sub Minim' }
            ].map(tab => (
              <Button
                key={tab.key}
                variant={activeSubTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveSubTab(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Raport Consumabile</CardTitle>
              <CardDescription>
                {activeSubTab === 'importance' && 'Cereri de consumabile dupa importanta'}
                {activeSubTab === 'status' && 'Cereri de consumabile dupa status'}
                {activeSubTab === 'lowstock' && 'Articole cu stoc sub nivelul minim'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
                </div>
              ) : (
                <>
                  {activeSubTab === 'lowstock' && reportData?.data ? (
                    <div className="space-y-3">
                      {reportData.data.length === 0 ? (
                        <p className="text-center py-8 text-green-600">Toate articolele au stoc suficient!</p>
                      ) : (
                        <>
                          <div className="bg-red-50 p-3 rounded-lg mb-4">
                            <p className="text-red-700 font-medium">{reportData.count} articole cu stoc insuficient</p>
                          </div>
                          {reportData.data.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <div>
                                <p className="font-medium">{item.name}</p>
                                <p className="text-sm text-slate-500">Cod: {item.code}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm">Stoc: <strong className="text-red-600">{item.current_stock}</strong> / {item.min_stock}</p>
                                <p className="text-xs text-slate-500">Deficit: {item.deficit}</p>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      {reportData?.chart_data && renderBarChart(reportData.chart_data)}
                      {reportData?.chart_data && renderPieChart(reportData.chart_data)}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SLA Reports */}
        <TabsContent value="sla" className="mt-4">
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle>Raport SLA</CardTitle>
              <CardDescription>Status conformitate SLA pentru tichete</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
                </div>
              ) : reportData ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-green-700">{reportData.ok?.length || 0}</p>
                        <p className="text-sm text-green-600">SLA OK</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50 border-orange-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-orange-700">{reportData.at_risk?.length || 0}</p>
                        <p className="text-sm text-orange-600">In Risc</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-red-700">{reportData.breached?.length || 0}</p>
                        <p className="text-sm text-red-600">Depasit</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {reportData.breached?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-red-700">Tichete cu SLA Depasit:</h4>
                      {reportData.breached.map((ticket, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div>
                            <p className="font-medium">{ticket.title}</p>
                            <p className="text-sm text-slate-500">Deadline: {new Date(ticket.deadline).toLocaleString('ro-RO')}</p>
                          </div>
                          <Badge className="bg-red-100 text-red-700">{ticket.priority}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-500">Nu exista date</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
